import { ResolvedConfig } from '../utils/config.js';
import path from 'path';
import { FrontmatterMetadata, ValidationResult } from '../types.js';
import { PluginService } from './plugin-service.js';
import { logger } from '../utils/logger.js';

export interface FileLocation {
  dirname: string;
  basename: string;
}

export interface FileState {
  pathname: string;
  metadata: FrontmatterMetadata;
}

export interface FileIntent {
  targetDir: string;
  metadata: FrontmatterMetadata;
}

export interface InterpretedIntent {
  targetLocation: FileLocation;
  targetMetadata: FrontmatterMetadata;
  reason: string;
}

export interface LocationPattern {
  inDirectory?: string | ((dir: string) => boolean);
  hasTypeDir?: boolean;
}

export interface MetadataPattern {
  hasState?: boolean;
  specificState?: string | ((state: string) => boolean);
}

export interface IntentPattern {
  pattern: {
    location?: LocationPattern;
    metadata?: MetadataPattern;
  };
  interpretation: (state: FileState) => InterpretedIntent;
}

export class IntentService {
  private intentPatterns: IntentPattern[];
  private validTypes: string[];

  constructor(
    private config: ResolvedConfig,
    private pluginService?: PluginService
  ) {
    this.intentPatterns = [];
    
    // Initialize valid types from config first
    this.validTypes = [...config.types_list];
    logger.debug('Initial validTypes:', this.validTypes);
    
    // Ensure 'issue' is a valid type if no types are configured
    if (this.validTypes.length === 0) {
      this.validTypes = ['issue'];
      logger.debug('No types configured, using default:', this.validTypes);
    }
    // Add 'issue' as a default type if not already present
    else if (!this.validTypes.includes('issue')) {
      this.validTypes.push('issue');
      logger.debug('Added issue type:', this.validTypes);
    }
    
    // Initialize plugin service with core states if provided
    if (this.pluginService) {
      logger.debug('Registering core types with plugin service:', this.validTypes);
      // Register core states from config
      this.pluginService.registerCoreTypes(this.validTypes);
      this.pluginService.registerCoreActiveStates(this.config.active_states);
      this.pluginService.registerCoreInactiveReasons(this.config.inactive_reasons);
      
      // Get plugin types and combine with our existing types
      const pluginTypes = this.pluginService.getValidTypes();
      logger.debug('Plugin types:', pluginTypes);
      
      // Combine all types while preserving uniqueness
      this.validTypes = [...new Set([...this.validTypes, ...pluginTypes])];
      logger.debug('Final validTypes:', this.validTypes);
      
      // Apply plugin patterns
      this.intentPatterns = this.pluginService.applyPluginIntents(this.intentPatterns);
    }
  }

  /**
   * Get all registered intent patterns, including plugin-provided ones
   */
  getIntentPatterns(): IntentPattern[] {
    return [...this.intentPatterns];
  }

  isValidState(state: string): boolean {
    if (!state) return false;

    // Get all valid states including plugin-provided ones
    const validActiveStates = this.pluginService 
      ? this.pluginService.getValidActiveStates()
      : this.config.active_states;

    const validInactiveReasons = this.pluginService
      ? this.pluginService.getValidInactiveReasons()
      : this.config.inactive_reasons;

    const normalizedState = state.toLowerCase();
    logger.debug('isValidState - Checking state:', state);
    logger.debug('isValidState - Valid active states:', validActiveStates);
    logger.debug('isValidState - Valid inactive reasons:', validInactiveReasons);
    
    return validActiveStates.includes(normalizedState) || validInactiveReasons.includes(normalizedState);
  }

  private isValidInactiveReason(reason: string): boolean {
    // Get all valid inactive reasons including plugin-provided ones
    const validReasons = this.pluginService
      ? this.pluginService.getValidInactiveReasons()
      : this.config.inactive_reasons;

    return validReasons.includes(reason.toLowerCase());
  }

  private isValidType(type: string): boolean {
    if (!type) return false;
    
    // Always allow 'issue' type
    if (type.toLowerCase() === 'issue') return true;
    
    // Normalize the type for comparison
    const normalizedType = type.toLowerCase();
    logger.debug('isValidType - Checking type:', type);
    logger.debug('isValidType - Valid types:', this.validTypes);
    
    // Check against our combined list of valid types
    return this.validTypes.some(t => t.toLowerCase() === normalizedType);
  }

  isActiveState(state: string): boolean {
    if (!state) return false;
    
    // Check if it's an active state from config or plugins
    const validStates = this.pluginService 
      ? this.pluginService.getValidActiveStates()
      : this.config.active_states;

    logger.debug('isActiveState - Checking state:', state);
    logger.debug('isActiveState - Valid states:', validStates);
    
    return validStates.includes(state.toLowerCase());
  }

  isInactiveReason(state: string): boolean {
    if (!state) return false;

    // Check if it's an inactive reason from config or plugins
    const validReasons = this.pluginService
      ? this.pluginService.getValidInactiveReasons()
      : this.config.inactive_reasons;

    logger.debug('isInactiveReason - Checking state:', state);
    logger.debug('isInactiveReason - Valid reasons:', validReasons);
    
    return validReasons.includes(state.toLowerCase());
  }

  getCanonicalState(state: string): string {
    // Get all valid states including plugin-provided ones
    const validActiveStates = this.pluginService 
      ? this.pluginService.getValidActiveStates()
      : this.config.active_states;

    const validInactiveReasons = this.pluginService
      ? this.pluginService.getValidInactiveReasons()
      : this.config.inactive_reasons;

    // Preserve user's case if it exactly matches a configured state
    const exactActiveMatch = validActiveStates.find(s => s === state);
    if (exactActiveMatch) return exactActiveMatch;

    const exactInactiveMatch = validInactiveReasons.find(r => r === state);
    if (exactInactiveMatch) return exactInactiveMatch;

    // Otherwise find case-insensitive match
    const activeMatch = validActiveStates.find(s => s.toLowerCase() === state.toLowerCase());
    if (activeMatch) return activeMatch;

    const inactiveMatch = validInactiveReasons.find(r => r.toLowerCase() === state.toLowerCase());
    if (inactiveMatch) return inactiveMatch;

    return state; // Let validation catch invalid states
  }

  getTargetDirForState(state: string, type: string): string {
    // Ensure we're working with relative paths
    const rootDir = this.config.issues_dir.replace(/^\/+/, '');
    const inboxDir = this.config.inbox_dir.replace(/^\/+/, '');

    // For active states, use the state directory
    if (this.isActiveState(state)) {
      return path.join(rootDir, `_${state}`);
    }

    // For inactive states, use the type archive directory
    if (this.isInactiveReason(state)) {
      return path.join(rootDir, type);
    }

    // Default to inbox
    return inboxDir;
  }

  getTypeArchiveDir(type: string): string {
    // Ensure we're working with relative paths
    const rootDir = this.config.issues_dir.replace(/^\/+/, '');
    return path.join(rootDir, type);
  }

  matchState(state: FileState): { matches: boolean; state?: string } {
    if (!state.pathname || !state.metadata) {
      return { matches: false };
    }

    const normalizedPathname = state.pathname.toLowerCase();

    // First check metadata state if present
    if (state.metadata.state) {
      const canonicalState = this.getCanonicalState(state.metadata.state);
      const isActive = this.isActiveState(canonicalState);

      // For active states, check if in correct directory
      if (isActive) {
        if (normalizedPathname.includes(`_${canonicalState.toLowerCase()}/`)) {
          return { matches: true, state: canonicalState };
        }
      } else {
        // For inactive states, check if in type directory
        if (normalizedPathname.includes(`/${state.metadata.type}/`)) {
          return { matches: true, state: canonicalState };
        }
      }
      return { matches: false };
    }

    // If no metadata state, check pathname patterns
    // Special case for inbox
    if (normalizedPathname === 'inbox' || normalizedPathname.includes('inbox/')) {
      return { matches: true, state: 'inbox' };
    }

    // Check for active states in underscore-prefixed directories
    for (const activeState of this.config.active_states) {
      const normalizedState = activeState.toLowerCase();
      if (normalizedPathname.includes(`_${normalizedState}/`)) {
        return { matches: true, state: activeState };
      }
    }

    // Check for inactive states in type subdirectories
    for (const inactiveReason of this.config.inactive_reasons) {
      const normalizedReason = inactiveReason.toLowerCase();
      if (normalizedPathname.includes(`/${normalizedReason}/`)) {
        return { matches: true, state: inactiveReason };
      }
    }

    return { matches: false };
  }

  interpretState(state: string): { canonicalState?: string; isActive: boolean } {
    // Handle inbox state - it's a special case that maps to active with no state
    if (state.toLowerCase() === 'inbox') {
      return { canonicalState: undefined, isActive: true };
    }

    // Get canonical form of state
    const canonicalState = this.getCanonicalState(state);

    // Check if it's an inactive reason - always set the state and mark as inactive
    if (this.isInactiveReason(canonicalState)) {
      return { canonicalState, isActive: false };
    }

    // Check if it's an active state
    if (this.isActiveState(canonicalState)) {
      return { canonicalState, isActive: true };
    }

    // If it's neither, return the state as is but mark as inactive
    return { canonicalState, isActive: false };
  }

  matchesState(metadata: FrontmatterMetadata, stateInfo: { isActive: boolean; canonicalState?: string }): boolean {
    // Special handling for inbox state
    if (stateInfo.canonicalState === undefined && stateInfo.isActive) {
      return metadata.active === true && !metadata.state;
    }

    // For states, check both the state field and active status
    return metadata.state === stateInfo.canonicalState && metadata.active === stateInfo.isActive;
  }

  interpretIntent(state: FileState): InterpretedIntent {
    const { type } = state.metadata;

    // Handle state-based intents first
    if (state.metadata.state) {
      // Special handling for inbox state
      if (state.metadata.state.toLowerCase() === 'inbox') {
        const inboxIntent = {
          targetLocation: {
            dirname: this.config.inbox_dir || 'inbox',
            basename: `${type}-${state.metadata.id}.md`
          },
          targetMetadata: {
            ...state.metadata,
            type,  // Ensure type is set
            state: undefined,
            active: true
          },
          reason: 'Inbox state specified - normalizing to active with no state'
        };
        return inboxIntent;
      }

      // Get target directory based on state
      const targetDir = this.getTargetDirForState(state.metadata.state, type);
      const isActive = this.isActiveState(state.metadata.state);

      return {
        targetLocation: {
          dirname: targetDir,
          basename: `${state.metadata.state}-${type}-${state.metadata.id}.md`
        },
        targetMetadata: {
          ...state.metadata,
          type,  // Ensure type is set
          state: state.metadata.state,
          active: isActive
        },
        reason: `File has state '${state.metadata.state}' - moving to appropriate directory`
      };
    }

    // For items with no state, check active flag
    if (state.metadata.active === true) {
      // Active items with no state go to inbox
      return {
        targetLocation: {
          dirname: this.config.inbox_dir || 'inbox',
          basename: `${type}-${state.metadata.id}.md`
        },
        targetMetadata: {
          ...state.metadata,
          type,  // Ensure type is set
          state: undefined,
          active: true
        },
        reason: 'Active item with no state - moving to inbox'
      };
    }

    // For inactive items with no state, move to type directory
    if (state.metadata.active === false) {
      const baseDir = this.config.issues_dir || 'issues';
      return {
        targetLocation: {
          dirname: path.join(baseDir, type),
          basename: `${type}-${state.metadata.id}.md`
        },
        targetMetadata: {
          ...state.metadata,
          type,  // Ensure type is set
          active: false,
          state: undefined
        },
        reason: 'Inactive item with no state - moving to type archive'
      };
    }

    // Default to inbox for new items
    return {
      targetLocation: {
        dirname: this.config.inbox_dir || 'inbox',
        basename: `${type}-${state.metadata.id}.md`
      },
      targetMetadata: {
        ...state.metadata,
        type,  // Ensure type is set
        state: undefined,
        active: true
      },
      reason: 'New item - moving to inbox'
    };
  }

  validateFinalState(state: FileState): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate metadata has required fields
    if (!state.metadata.id) {
      errors.push('Missing required field: id');
    }
    if (!state.metadata.type) {
      errors.push('Missing required field: type');
    }

    // Validate type is valid
    if (state.metadata.type && !this.isValidType(state.metadata.type)) {
      errors.push(`Invalid type: ${state.metadata.type}`);
    }

    // Validate state if present
    if (state.metadata.state && !this.isValidState(state.metadata.state)) {
      errors.push(`Invalid state: ${state.metadata.state}`);
    }

    // Validate active status is consistent with state
    if (state.metadata.state) {
      const isActive = this.isActiveState(state.metadata.state);
      if (state.metadata.active === undefined) {
        warnings.push('Active status not explicitly set - inferring from state');
      } else if (state.metadata.active !== isActive) {
        errors.push(`Active status (${state.metadata.active}) does not match state type (${isActive ? 'active' : 'inactive'})`);
      }

      // Check if file location matches state
      const targetDir = this.getTargetDirForState(state.metadata.state, state.metadata.type);
      const currentDir = path.dirname(state.pathname);
      if (currentDir !== targetDir) {
        warnings.push('File location does not match state');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  validateStateTransition(initialState: FileState, targetState: FileState): ValidationResult {
    const result: ValidationResult = {
      valid: true,
      errors: [],
      warnings: [],
      guidance: []
    };

    // First validate the target state
    const finalStateValidation = this.validateFinalState(targetState);
    if (finalStateValidation.warnings) {
      result.warnings!.push(...finalStateValidation.warnings);
    }

    // Check metadata preservation
    const preservedFields = ['id', 'type', 'title', 'created'];
    for (const field of preservedFields) {
      if (initialState.metadata[field] && initialState.metadata[field] !== targetState.metadata[field]) {
        result.errors.push(`Field '${field}' must be preserved during state transition`);
        result.valid = false;
      }
    }

    // Check filename pattern
    const expectedBasename = targetState.metadata.state
      ? `${targetState.metadata.state}-${targetState.metadata.type}-${targetState.metadata.id}`
      : `${targetState.metadata.type}-${targetState.metadata.id}`;

    if (targetState.pathname !== expectedBasename) {
      result.guidance!.push(`Filename '${targetState.pathname}' doesn't match expected pattern '${expectedBasename}' - will be renamed`);
    }

    return result;
  }

  validateMetadataAgainstConfig(metadata: FrontmatterMetadata): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate type is valid
    if (metadata.type && !this.isValidType(metadata.type)) {
      errors.push(`Invalid type: ${metadata.type}`);
    }

    // Validate state if present
    if (metadata.state && !this.isValidState(metadata.state)) {
      errors.push(`Invalid state: ${metadata.state}`);
    }

    // Validate active status is consistent with state
    if (metadata.state) {
      const isActive = this.isActiveState(metadata.state);
      if (metadata.active === undefined) {
        warnings.push('Active status not explicitly set - inferring from state');
      } else if (metadata.active !== isActive) {
        errors.push(`Active status (${metadata.active}) does not match state type (${isActive ? 'active' : 'inactive'})`);
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  validateMove(sourcePath: string, targetPath: string, metadata: FrontmatterMetadata): ValidationResult {
    // Get current metadata from source path
    const sourceState: FileState = {
      pathname: sourcePath,
      metadata: { ...metadata }  // Use current metadata as base
    };

    // Create target state with new metadata
    const targetState: FileState = {
      pathname: targetPath,
      metadata
    };

    // First validate both states individually
    const sourceValidation = this.validateFinalState(sourceState);
    if (!sourceValidation.valid) {
      return sourceValidation;
    }

    const targetValidation = this.validateFinalState(targetState);
    if (!targetValidation.valid) {
      return targetValidation;
    }

    // Then validate the transition between them
    const transitionValidation = this.validateStateTransition(sourceState, targetState);
    
    // Combine warnings from all validations
    const allWarnings = [
      ...(sourceValidation.warnings || []),
      ...(targetValidation.warnings || []),
      ...(transitionValidation.guidance || [])
    ];

    return {
      valid: transitionValidation.valid,
      errors: transitionValidation.errors,
      warnings: [],  // Add empty warnings array since it's required
      guidance: allWarnings.length > 0 ? allWarnings : undefined
    };
  }

  /**
   * Clean state transition by removing invalid fields and normalizing values
   */
  cleanStateTransition(current: FrontmatterMetadata, next: Partial<FrontmatterMetadata>): FrontmatterMetadata {
    logger.debug('cleanStateTransition - Current metadata:', current);
    logger.debug('cleanStateTransition - Next metadata:', next);
    
    // Start with current metadata to preserve all fields
    const result = { ...current };

    // Handle active flag first
    if (next.active !== undefined) {
      logger.debug('cleanStateTransition - Setting active:', next.active);
      result.active = next.active;

      // If setting to active
      if (next.active === true) {
        // Clear any existing state when setting to active
        result.state = undefined;
      }
    }

    // Then handle state changes
    if (next.state !== undefined) {
      logger.debug('cleanStateTransition - Processing state:', next.state);
      const state = this.getCanonicalState(next.state);
      logger.debug('cleanStateTransition - Canonical state:', state);
      result.state = state;
      
      // If transitioning to an inactive reason, always set active to false
      if (this.isInactiveReason(state)) {
        logger.debug('cleanStateTransition - State is inactive reason, setting active to false');
        result.active = false;
      } else if (this.isActiveState(state)) {
        logger.debug('cleanStateTransition - State is active state, setting active to true');
        result.active = true;
      }
    }

    // Then process other required fields to ensure they're normalized
    if (next.type !== undefined) {
      logger.debug('cleanStateTransition - Setting type:', next.type);
      result.type = next.type?.toLowerCase();
    }
    if (next.id !== undefined) {
      logger.debug('cleanStateTransition - Setting id:', next.id);
      result.id = next.id;
    }

    logger.debug('cleanStateTransition - Final result:', result);
    return result;
  }

  /**
   * Interprets metadata and infers active state
   */
  interpretMetadata(metadata: Partial<FrontmatterMetadata>): FrontmatterMetadata {
    // Create a copy to avoid modifying input
    const result = { ...metadata } as FrontmatterMetadata;

    // Handle state interpretation
    if (result.state) {
      const { canonicalState, isActive } = this.interpretState(result.state);
      result.state = canonicalState;
      result.active = isActive;
    }

    // Handle type interpretation
    if (!result.type) {
      // If no type is provided, use 'issue' as default
      result.type = 'issue';
    } else {
      // Normalize type to lowercase
      result.type = result.type.toLowerCase();
    }

    // Validate type
    if (!this.isValidType(result.type)) {
      throw new Error(`Invalid type: ${result.type}`);
    }

    return result;
  }

  /**
   * Interprets a state transition and returns the necessary metadata updates
   */
  interpretStateTransition(targetState: string, options: { reason?: string } = {}): Partial<FrontmatterMetadata> {
    const stateInfo = this.interpretState(targetState);
    
    return {
      active: stateInfo.isActive,
      state: stateInfo.canonicalState
    };
  }

  // Add a method to get the valid types
  getValidTypes(): string[] {
    return this.validTypes;
  }
}